import type { ReactNode } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AmountPreview } from './AmountPreview';
import { Segmented } from './ui/segmented';

/**
 * An amount with its currency, for forms with several amounts (Exchange, paid in another
 * currency): a label, a large amount input and the currency as a segmented control (or a pill
 * when the account holds only one).
 */
export function MoneyField({
  id,
  label,
  hint,
  register,
  currency,
  currencies,
  onCurrency,
  error,
  preview,
  footer,
}: {
  id: string;
  label: string;
  hint?: string;
  register: UseFormRegisterReturn;
  currency: string;
  currencies: readonly string[];
  onCurrency: (currency: string) => void;
  error?: string | null;
  /** What was typed, to show it written out and in the base currency underneath. */
  preview?: { raw: string; baseCurrency: string; rate: string | null | undefined };
  /** Under the amount, e.g. the account a payment line came from. */
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 rounded-3xl border border-line bg-surface p-4">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-[13px] font-semibold text-muted uppercase">
          {label}
        </label>
        {hint && <span className="text-xs text-muted">{hint}</span>}
      </div>
      <div className="flex items-center gap-3">
        <input
          id={id}
          inputMode={currency === 'LBP' ? 'text' : 'decimal'}
          autoComplete="off"
          dir="ltr"
          placeholder={currency === 'LBP' ? '500k' : '0.00'}
          aria-invalid={error ? true : undefined}
          className="w-full min-w-0 flex-1 bg-transparent text-[32px] leading-tight font-semibold tracking-[-0.5px] tabular-nums placeholder:text-muted focus-visible:outline-none"
          {...register}
        />
        {currencies.length > 1 ? (
          <div className="w-32 shrink-0">
            <Segmented
              label={t('add.currency')}
              options={currencies.map((c) => ({ value: c, label: c }))}
              value={currency}
              onChange={onCurrency}
              compact
            />
          </div>
        ) : (
          <span className="shrink-0 rounded-full bg-surface-2 px-3 py-1 text-sm font-semibold">
            {currency}
          </span>
        )}
      </div>
      {preview && (
        <div aria-live="polite">
          <AmountPreview currency={currency} {...preview} />
        </div>
      )}
      {error && <p className="text-sm text-danger">{error}</p>}
      {footer}
    </div>
  );
}
